import React from 'react';
import { getCoachingPersonality } from '../services/lumoPersonalityService';

/**
 * CoachingStageIndicator Component
 * Displays the current coaching personality stage (Mentor/Challenger/Sparring Partner)
 * Shows visual feedback on Lumo's current coaching mode
 * 
 * @param {string} skillLevel - User's skill level (beginner/intermediate/advanced)
 * @param {string} variant - Display variant ('badge', 'card', 'inline')
 * @param {boolean} showDescription - Whether to show stage description
 */
const CoachingStageIndicator = ({ 
  skillLevel = 'beginner', 
  variant = 'badge',
  showDescription = false 
}) => {
  const personality = getCoachingPersonality(skillLevel);
  
  // Stage color mapping
  const stageColors = {
    1: 'bg-green-500/10 text-green-600 border-green-500/30', // Mentor Mode
    2: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30', // Challenger Mode
    3: 'bg-red-500/10 text-red-600 border-red-500/30' // Sparring Partner Mode
  };

  const stageIconBg = {
    1: 'bg-green-500',
    2: 'bg-yellow-500',
    3: 'bg-red-500'
  };

  if (variant === 'badge') {
    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${stageColors[personality.stage]}`}>
        <span className="text-xl">{personality.icon}</span>
        <span className="text-sm font-medium">{personality.name}</span>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className="inline-flex items-center gap-2">
        <div className={`w-8 h-8 rounded-lg ${stageIconBg[personality.stage]} flex items-center justify-center`}>
          <span className="text-lg">{personality.icon}</span>
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{personality.name}</p>
          {showDescription && (
            <p className="text-xs text-muted-foreground">{personality.description}</p>
          )}
        </div>
      </div>
    );
  }

  // Default: 'card' variant
  return (
    <div className={`p-4 rounded-xl border ${stageColors[personality.stage]}`}>
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-xl ${stageIconBg[personality.stage]} flex items-center justify-center flex-shrink-0`}>
          <span className="text-2xl">{personality.icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-foreground">{personality.name}</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-background/50 text-muted-foreground">
              Stage {personality.stage}
            </span>
          </div>
          <p className="text-sm text-foreground/80 mb-2">{personality.description}</p>
          {showDescription && (
            <div className="text-xs text-muted-foreground">
              <p><span className="font-semibold">Coaching Tone:</span> {personality.tone}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * CoachingStageProgress Component
 * Shows progression path and next stage milestone
 */
export const CoachingStageProgress = ({ level, skillLevel }) => {
  const currentPersonality = getCoachingPersonality(skillLevel);
  
  // Determine next stage and milestone
  const stages = [
    { level: 1, name: 'Mentor Mode', skillLevel: 'beginner', icon: '🌱' },
    { level: 5, name: 'Challenger Mode', skillLevel: 'intermediate', icon: '⚡' },
    { level: 10, name: 'Sparring Partner Mode', skillLevel: 'advanced', icon: '🥊' }
  ];

  const currentStageIndex = stages.findIndex(s => s.skillLevel === skillLevel);
  const nextStage = stages[currentStageIndex + 1];

  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">Coaching Evolution</h3>
        <span className="text-xs text-muted-foreground">Level {level}</span>
      </div>

      {/* Stage progression timeline */}
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          {stages.map((stage, index) => {
            const isActive = index <= currentStageIndex;
            const isCurrent = stage.skillLevel === skillLevel;
            
            return (
              <div key={stage.level} className="flex flex-col items-center flex-1">
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                    isCurrent 
                      ? 'bg-primary text-white ring-4 ring-primary/20 scale-110' 
                      : isActive 
                        ? 'bg-primary/20 text-primary' 
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {stage.icon}
                </div>
                <p className={`text-xs mt-2 text-center ${isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                  {stage.name}
                </p>
                <p className="text-xs text-muted-foreground">Lvl {stage.level}+</p>
              </div>
            );
          })}
        </div>

        {/* Connecting line */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-muted -z-10">
          <div 
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${(currentStageIndex / (stages.length - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Next milestone */}
      {nextStage && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground mb-1">Next Unlock:</p>
          <div className="flex items-center gap-2">
            <span className="text-lg">{nextStage.icon}</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{nextStage.name}</p>
              <p className="text-xs text-muted-foreground">Reach Level {nextStage.level}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-primary">{nextStage.level - level} levels</p>
              <p className="text-xs text-muted-foreground">to go</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachingStageIndicator;
